import React, { useEffect, useRef, useState, useMemo } from "react";
import Modal from "react-modal";
import {
  FaChevronLeft,
  FaChevronRight,
  FaPlay,
  FaChevronDown,
  FaXmark,
  FaFilm,
} from "react-icons/fa6";
import { useHomeContext } from "../../../context/Home_context";
import { getEmbedUrl, getYouTubeThumbnail } from "../../../utils/helper";
import "../../home/homevideo/HomeVideo.css";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";

Modal.setAppElement("#root");

const EditionVideo = () => {
  const { edition_expo_data } = useHomeContext();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(null);
  const [activeEdition, setActiveEdition] = useState(null);

  const curtainRaiserDesc =
    edition_expo_data?.curtain_Raiser_description ||
    edition_expo_data?.curtain_raiser_description ||
    "";

  const voiceDesc = edition_expo_data?.voice_description || "";

  const [activeModalVideo, setActiveModalVideo] = useState(null);
  const [activeVIPIndex, setActiveVIPIndex] = useState(0);
  const [activeVoiceIndex, setActiveVoiceIndex] = useState(0);
  const tabsTrackRef = useRef(null);

  useEffect(() => {
    if (activeModalVideo) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeModalVideo]);

  // Edition/Curtain Raiser tabs from API (raiser_categories)
  const editionTabs = useMemo(() => {
    if (
      edition_expo_data?.raiser_categories &&
      Array.isArray(edition_expo_data.raiser_categories) &&
      edition_expo_data.raiser_categories.length > 0
    ) {
      return edition_expo_data.raiser_categories.map((cat) => ({
        id: cat.id,
        label: cat.name,
      }));
    }
    return [];
  }, [edition_expo_data?.raiser_categories]);

  useEffect(() => {
    if (
      editionTabs.length > 0 &&
      (activeEdition === null || typeof activeEdition === "string")
    ) {
      const firstId = editionTabs[0].id;
      setActiveEdition(firstId);
    }
  }, [editionTabs]);

  const handleEditionTabClick = (tabId) => {
    setActiveEdition(tabId);
    setActiveVIPIndex(0);
  };

  // Voice tabs from API (voice_categories)
  const filterTabs = useMemo(() => {
    if (
      edition_expo_data?.voice_categories &&
      Array.isArray(edition_expo_data.voice_categories) &&
      edition_expo_data.voice_categories.length > 0
    ) {
      return edition_expo_data.voice_categories.map((cat) => ({
        id: cat.id,
        name: cat.name,
        label: cat.name,
      }));
    }
    return [];
  }, [edition_expo_data?.voice_categories]);

  useEffect(() => {
    if (filterTabs.length > 0 && activeTab === null) {
      const firstId = filterTabs[0].id;
      setActiveTab(firstId);
    }
  }, [filterTabs]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setActiveVoiceIndex(0);
  };

  // Process Curtain Raiser videos from raiser_categories matching activeEdition
  const activeVideos = useMemo(() => {
    let rawItems = [];

    if (
      edition_expo_data?.raiser_categories &&
      Array.isArray(edition_expo_data.raiser_categories) &&
      edition_expo_data.raiser_categories.length > 0
    ) {
      const currentCat =
        edition_expo_data.raiser_categories.find(
          (cat) => cat.id === activeEdition,
        ) || edition_expo_data.raiser_categories[0];

      if (
        currentCat &&
        currentCat.raisers &&
        Array.isArray(currentCat.raisers)
      ) {
        currentCat.raisers.forEach((raiser) => {
          if (raiser?.childs && Array.isArray(raiser.childs)) {
            rawItems.push(...raiser.childs);
          } else if (raiser?.data && Array.isArray(raiser.data)) {
            rawItems.push(...raiser.data);
          } else if (
            raiser?.video_link ||
            raiser?.name ||
            raiser?.description
          ) {
            rawItems.push(raiser);
          }
        });
      }
    }

    if (rawItems.length > 0) {
      return rawItems.map((item) => ({
        id: item.id || item.name,
        title:
          item.description ||
          item.curtain_Raiser_description ||
          item.curtain_raiser_description ||
          item.title ||
          item.name ||
          "",
        speaker: item.name || item.speaker || "",
        role: item.designation || item.role || "",
        thumbnail:
          item.image_full_path ||
          item.thumbnail_full_path ||
          item.thumbnail ||
          getYouTubeThumbnail(
            item.video_link || item.video_url || item.videoUrl,
          ),
        videoUrl:
          item.video_link || item.video_url || item.videoUrl || item.link || "",
      }));
    }

    return [];
  }, [edition_expo_data?.raiser_categories, activeEdition]);

  // Auto-scroll active speaker tab into center view
  useEffect(() => {
    if (tabsTrackRef.current && editionTabs.length > 0) {
      const activeIdx = editionTabs.findIndex(
        (tab) => tab.id === activeEdition,
      );
      if (activeIdx !== -1) {
        const container = tabsTrackRef.current;
        const activeEl = container.children[activeIdx];
        if (activeEl) {
          const containerWidth = container.offsetWidth;
          const elLeft = activeEl.offsetLeft;
          const elWidth = activeEl.offsetWidth;
          container.scrollTo({
            left: elLeft - containerWidth / 2 + elWidth / 2,
            behavior: "smooth",
          });
        }
      }
    }
  }, [activeEdition, editionTabs]);

  const handleScrollTabs = (direction) => {
    if (tabsTrackRef.current) {
      const scrollAmount = direction === "left" ? -240 : 240;
      tabsTrackRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Process Voice videos from voice_categories matching activeTab
  const voiceVideoList = useMemo(() => {
    let rawItems = [];

    if (
      edition_expo_data?.voice_categories &&
      Array.isArray(edition_expo_data.voice_categories) &&
      edition_expo_data.voice_categories.length > 0
    ) {
      const currentCat =
        edition_expo_data.voice_categories.find((cat) => cat.id === activeTab) ||
        edition_expo_data.voice_categories[0];

      if (currentCat && currentCat.voices && Array.isArray(currentCat.voices)) {
        currentCat.voices.forEach((voice) => {
          if (voice?.childs && Array.isArray(voice.childs)) {
            rawItems.push(...voice.childs);
          } else if (voice?.data && Array.isArray(voice.data)) {
            rawItems.push(...voice.data);
          } else if (voice?.video_link || voice?.name || voice?.description) {
            rawItems.push(voice);
          }
        });
      }
    }

    if (rawItems.length > 0) {
      return rawItems.map((item) => ({
        id: item.id || item.name,
        title:
          item.description ||
          item.voice_description ||
          item.title ||
          item.name ||
          "Bharat Buildcon Video",
        speaker: item.name || item.speaker || "",
        role: item.designation || item.role || "",
        thumbnail:
          item.image_full_path ||
          item.thumbnail_full_path ||
          item.thumbnail ||
          getYouTubeThumbnail(
            item.video_link || item.video_url || item.videoUrl,
          ),
        videoUrl:
          item.video_link ||
          item.video_url ||
          item.videoUrl ||
          item.link ||
          "https://www.youtube.com/watch?v=FAC42mTN548",
      }));
    }

    return [];
  }, [edition_expo_data?.voice_categories, activeTab]);

  const handlePrevVIP = () => {
    if (activeVideos.length === 0) return;
    setActiveVIPIndex((prev) =>
      prev === 0 ? activeVideos.length - 1 : prev - 1,
    );
  };

  const handleNextVIP = () => {
    if (activeVideos.length === 0) return;
    setActiveVIPIndex((prev) =>
      prev === activeVideos.length - 1 ? 0 : prev + 1,
    );
  };

  const handlePrevVoice = () => {
    if (voiceVideoList.length === 0) return;
    setActiveVoiceIndex((prev) =>
      prev === 0 ? voiceVideoList.length - 1 : prev - 1,
    );
  };

  const handleNextVoice = () => {
    if (voiceVideoList.length === 0) return;
    setActiveVoiceIndex((prev) =>
      prev === voiceVideoList.length - 1 ? 0 : prev + 1,
    );
  };

  return (
    <section className="hv-section" id="videos">
      {/* Indian Flag Background Mesh & Waving Ribbons */}
      <div className="hv-bg-flag-wrapper" aria-hidden="true">
        <div className="hv-flag-saffron-aura" />
        <div className="hv-flag-green-aura" />

        <svg
          className="hv-flag-svg-ribbon"
          viewBox="0 0 1440 650"
          fill="none"
          preserveAspectRatio="none"
        >
          <path
            d="M0,140 C320,270 500,50 900,190 C1200,300 1350,100 1440,140 L1440,0 L0,0 Z"
            fill="url(#saffronWave)"
            opacity="0.3"
          />
          <path
            d="M0,420 C420,260 650,520 1050,380 C1280,290 1390,440 1440,400 L1440,650 L0,650 Z"
            fill="url(#greenWave)"
            opacity="0.25"
          />
          <defs>
            <linearGradient
              id="saffronWave"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop
                offset="0%"
                stopColor="var(--hh-saffron)"
                stopOpacity="0.4"
              />
              <stop
                offset="100%"
                stopColor="var(--hh-saffron-light)"
                stopOpacity="0.05"
              />
            </linearGradient>
            <linearGradient id="greenWave" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop
                offset="0%"
                stopColor="var(--hh-green)"
                stopOpacity="0.05"
              />
              <stop
                offset="100%"
                stopColor="var(--hh-ufi-green)"
                stopOpacity="0.35"
              />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="hv-container">
        {/* Header */}
        <header className="hv-header">
          <h2 className="hv-main-title">
            Curtain Raiser & <span>Voice of Bharat Buildcon</span>
          </h2>
          {curtainRaiserDesc && (
            <div
              className="hv-header-desc"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(curtainRaiserDesc) }}
            />
          )}
        </header>

        <div className="hv-3d-stage-wrapper">
          {/* Edition Tabs Navigation Shell (Dynamic Scroll Track) */}
          <div className="hv-3d-tabs-shell">
            <button
              type="button"
              className="hv-tabs-scroll-btn prev"
              onClick={() => handleScrollTabs("left")}
              aria-label="Scroll tabs left"
            >
              <FaChevronLeft size={14} />
            </button>

            <div className="hv-3d-speaker-tabs" ref={tabsTrackRef}>
              {editionTabs.map((edition) => (
                <button
                  key={edition.id}
                  type="button"
                  className={`hv-3d-tab-btn ${activeEdition === edition.id ? "active" : ""}`}
                  onClick={() => handleEditionTabClick(edition.id)}
                >
                  <span className="hv-3d-tab-dot" />
                  <span className="hv-3d-tab-speaker">{edition.label}</span>
                </button>
              ))}
            </div>

            <button
              type="button"
              className="hv-tabs-scroll-btn next"
              onClick={() => handleScrollTabs("right")}
              aria-label="Scroll tabs right"
            >
              <FaChevronRight size={14} />
            </button>
          </div>

          {/* 3D Floating Cards Container */}
          <div
            className={`hv-3d-cards-track ${activeVideos.length === 2 ? "has-two-cards" : ""}`}
          >
            {activeVideos.length === 0 ? (
              <div className="hv-no-videos-wrapper">
                <FaFilm className="hv-no-videos-big-icon" />
                <p className="hv-no-videos-simple-text">
                  No Videos Available in this Category
                </p>
              </div>
            ) : (
              <>
                {/* Left Nav Arrow */}
                {activeVideos.length > 1 && (
                  <button
                    type="button"
                    className="hv-3d-arrow hv-3d-arrow-prev"
                    onClick={handlePrevVIP}
                    aria-label="Previous Keynote"
                  >
                    <FaChevronLeft size={18} />
                  </button>
                )}

                {activeVideos.map((video, index) => {
                  const total = activeVideos.length;
                  let posClass = "hidden";

                  if (total === 1) {
                    posClass = "active-center pos-single";
                  } else if (total === 2) {
                    const isCurrentActive = index === activeVIPIndex;
                    const sideClass =
                      index === 0 ? "pos-two-left" : "pos-two-right";
                    const activeStateClass = isCurrentActive
                      ? "active-2"
                      : "inactive-2";
                    posClass = `${sideClass} ${activeStateClass}`;
                  } else {
                    const isCenter = index === activeVIPIndex;
                    const isLeft =
                      !isCenter &&
                      index === (activeVIPIndex - 1 + total) % total;
                    const isRight =
                      !isCenter &&
                      !isLeft &&
                      index === (activeVIPIndex + 1) % total;

                    if (isCenter) posClass = "active-center";
                    else if (isLeft) posClass = "pos-left";
                    else if (isRight) posClass = "pos-right";
                  }

                  const isInteractive =
                    total <= 2 ||
                    posClass.includes("active-center") ||
                    posClass.includes("active-2");

                  return (
                    <div
                      key={video.id || index}
                      className={`hv-3d-card ${posClass}`}
                      onClick={() => {
                        if (isInteractive) {
                          setActiveModalVideo(video);
                        } else {
                          setActiveVIPIndex(index);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (isInteractive) setActiveModalVideo(video);
                          else setActiveVIPIndex(index);
                        }
                      }}
                    >
                      <div className="hv-3d-thumb-wrap">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="hv-3d-thumb-img"
                        />
                        <div className="hv-3d-scrim" />

                        <div className="hv-3d-play-btn">
                          <FaPlay size={16} />
                        </div>
                      </div>

                      <div className="hv-3d-card-body">
                        <div className="hv-3d-speaker-row">
                          <span className="hv-3d-speaker-name">
                            {video.speaker}
                          </span>
                        </div>
                        <h3 className="hv-3d-card-title">{video.title}</h3>
                        <div className="hv-3d-card-footer">
                          <span className="hv-3d-role">{video.role}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Right Nav Arrow */}
                {activeVideos.length > 1 && (
                  <button
                    type="button"
                    className="hv-3d-arrow hv-3d-arrow-next"
                    onClick={handleNextVIP}
                    aria-label="Next Keynote"
                  >
                    <FaChevronRight size={18} />
                  </button>
                )}
              </>
            )}
          </div>

          {/* More Videos Button for Top Section (Curtain Raiser) */}
          <div className="hv-stage-actions-wrap">
            <button
              type="button"
              className="hv-btn-more-stage"
              onClick={() => navigate("/CurtainRaiser")}
            >
              <span>MORE VIDEOS</span>
              <FaChevronDown size={14} />
            </button>
          </div>
        </div>

        {/* -------------------------------------------------------------
            BOTTOM SECTION: VOICE OF BHARAT BUILDCON (3D Stage Carousel - 100% Same to Same)
        ------------------------------------------------------------- */}
        <div
          className="hv-3d-stage-wrapper"
          id="voice-of-bharat-section"
          style={{ marginTop: "2.5rem" }}
        >
          {/* Section Header */}
          <header className="hv-header">
            <h2 className="hv-main-title">
              Voice of <span>Bharat Buildcon</span>
            </h2>
            {voiceDesc && (
              <div
                className="hv-header-desc"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(voiceDesc) }}
              />
            )}
          </header>

          {/* 3D Glass Category Filter Tabs */}
          <div className="hv-3d-tabs-shell">
            <div className="hv-3d-speaker-tabs">
              {filterTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`hv-3d-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  <span className="hv-3d-tab-dot" />
                  <span className="hv-3d-tab-speaker">
                    {tab.name || tab.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* 3D Floating Cards Container */}
          <div
            className={`hv-3d-cards-track ${voiceVideoList.length === 2 ? "has-two-cards" : ""}`}
          >
            {voiceVideoList.length === 0 ? (
              <div className="hv-no-videos-wrapper">
                <FaFilm className="hv-no-videos-big-icon" />
                <p className="hv-no-videos-simple-text">
                  No Videos Available in this Category
                </p>
              </div>
            ) : (
              <>
                {/* Left Nav Arrow */}
                {voiceVideoList.length > 1 && (
                  <button
                    type="button"
                    className="hv-3d-arrow hv-3d-arrow-prev"
                    onClick={handlePrevVoice}
                    aria-label="Previous Video"
                  >
                    <FaChevronLeft size={18} />
                  </button>
                )}

                {voiceVideoList.map((video, index) => {
                  const total = voiceVideoList.length;
                  let posClass = "hidden";

                  if (total === 1) {
                    posClass = "active-center pos-single";
                  } else if (total === 2) {
                    const isCurrentActive = index === activeVoiceIndex;
                    const sideClass =
                      index === 0 ? "pos-two-left" : "pos-two-right";
                    const activeStateClass = isCurrentActive
                      ? "active-2"
                      : "inactive-2";
                    posClass = `${sideClass} ${activeStateClass}`;
                  } else {
                    const isCenter = index === activeVoiceIndex;
                    const isLeft =
                      !isCenter &&
                      index === (activeVoiceIndex - 1 + total) % total;
                    const isRight =
                      !isCenter &&
                      !isLeft &&
                      index === (activeVoiceIndex + 1) % total;

                    if (isCenter) posClass = "active-center";
                    else if (isLeft) posClass = "pos-left";
                    else if (isRight) posClass = "pos-right";
                  }

                  const isInteractive =
                    total <= 2 ||
                    posClass.includes("active-center") ||
                    posClass.includes("active-2");

                  return (
                    <div
                      key={video.id || index}
                      className={`hv-3d-card ${posClass}`}
                      onClick={() => {
                        if (isInteractive) {
                          setActiveModalVideo(video);
                        } else {
                          setActiveVoiceIndex(index);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (isInteractive) setActiveModalVideo(video);
                          else setActiveVoiceIndex(index);
                        }
                      }}
                    >
                      <div className="hv-3d-thumb-wrap">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="hv-3d-thumb-img"
                        />
                        <div className="hv-3d-scrim" />

                        <div className="hv-3d-play-btn">
                          <FaPlay size={16} />
                        </div>
                      </div>

                      <div className="hv-3d-card-body">
                        <div className="hv-3d-speaker-row">
                          <span className="hv-3d-speaker-name">
                            {video.speaker}
                          </span>
                        </div>
                        <h3 className="hv-3d-card-title">{video.title}</h3>
                        <div className="hv-3d-card-footer">
                          <span className="hv-3d-role">{video.role}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Right Nav Arrow */}
                {voiceVideoList.length > 1 && (
                  <button
                    type="button"
                    className="hv-3d-arrow hv-3d-arrow-next"
                    onClick={handleNextVoice}
                    aria-label="Next Video"
                  >
                    <FaChevronRight size={18} />
                  </button>
                )}
              </>
            )}
          </div>
          {/* More Videos Button for Bottom Section */}
          <div className="hv-stage-actions-wrap">
            <button type="button" className="hv-btn-more-stage">
              <span>MORE VIDEOS</span>
              <FaChevronDown size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* -------------------------------------------------------------
          VIDEO LIGHTBOX MODAL PLAYER (react-modal)
      ------------------------------------------------------------- */}
      <Modal
        isOpen={Boolean(activeModalVideo)}
        onRequestClose={() => setActiveModalVideo(null)}
        className="hv-modal-box"
        overlayClassName="hv-modal-backdrop"
        contentLabel={activeModalVideo?.title || "Video Player"}
        closeTimeoutMS={200}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        {activeModalVideo && (
          <div className="hv-modal-content">
            <div className="hv-modal-header">
              <h4 className="hv-modal-heading">{activeModalVideo.title}</h4>
              <button
                type="button"
                className="hv-modal-close-btn"
                onClick={() => setActiveModalVideo(null)}
                aria-label="Close"
              >
                <FaXmark size={16} />
              </button>
            </div>

            <div className="hv-modal-player-wrap">
              {activeModalVideo.videoUrl &&
              (activeModalVideo.videoUrl.endsWith(".mp4") ||
                activeModalVideo.videoUrl.startsWith("blob:") ||
                activeModalVideo.videoUrl.includes("/static/media/")) ? (
                <video
                  className="hv-modal-video"
                  src={activeModalVideo.videoUrl}
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <iframe
                  className="hv-modal-iframe"
                  src={getEmbedUrl(
                    activeModalVideo.videoUrl || activeModalVideo.embedUrl,
                  )}
                  title={activeModalVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default EditionVideo;
